import { SearchX } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import styled from 'styled-components';
import BrandCard from '@/components/brand-card';
import Loader from '@/components/loader';
import { Button, IconTile } from '@/components/ui';
import config from '@/config';
import { up } from '@/themes';
import { categoryInPhrase } from '@/utils/category';

const Container = styled.div`
  flex: 1;
  min-width: 0;
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  ${up.sm} {
    gap: 16px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${up.lg} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const EmptyState = styled.div`
  align-items: center;
  background-color: var(--card);
  border: 1px dashed var(--border);
  border-radius: var(--radius-2xl);
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 8px 0 32px;
  padding: 40px 24px;
  text-align: center;
`;

const Message = styled.div`
  color: var(--muted-foreground);
  font-size: 16px;
  line-height: 24px;
  max-width: 480px;

  p {
    line-height: inherit;
    margin: 0;
  }

  p + p {
    margin-top: 4px;
  }

  strong {
    color: var(--foreground);
    font-weight: 600;
  }
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const LoadingState = styled.div`
  height: 200px;
  margin: 50px 0;
  position: relative;
`;

const OtherBrandsTitle = styled.h3`
  border-bottom: 1px solid var(--border);
  color: var(--foreground);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: normal;
  line-height: 28px;
  margin: 0 0 16px;
  padding-bottom: 8px;
`;

const OtherBrands = styled.div`
  margin-top: 32px;
`;

const BrandGrid = ({
  brands,
  category,
  country,
  loading,
  onReset,
  otherBrands,
  priorityCount = 0,
  showEmptyState,
  showOtherBrands,
}) => {
  const otherGiftcards =
    Array.isArray(otherBrands) && otherBrands.filter((brand) => brand.type === 'giftcard');
  const otherTopups =
    Array.isArray(otherBrands) && otherBrands.filter((brand) => brand.type !== 'giftcard');
  const t = useTranslations('brandGrid');
  const cT = useTranslations('categories');
  const locale = useLocale();
  const strong = (children) => <strong>{children}</strong>;

  return (
    <Container>
      {showEmptyState && !loading && !brands.length && (
        <EmptyState>
          <IconTile>
            <SearchX aria-hidden="true" size={20} strokeWidth={1.75} />
          </IconTile>
          <Message>
            {(!showOtherBrands || !otherBrands.length) && (
              <>
                {!category && (
                  <p>{t.rich('noResultsInCountry', { country: country.name, strong })}</p>
                )}
                {category && (
                  <p>
                    {t.rich('noResultsInCategory', {
                      country: country.name,
                      category: categoryInPhrase(cT(category.slug || category.name), {
                        template: t.raw('noResultsInCategory'),
                        locale,
                        slug: category.slug,
                      }),
                      strong,
                    })}
                  </p>
                )}
              </>
            )}
            {showOtherBrands && !!otherBrands.length && (
              <>
                <p>{t.rich('noResultsInCountry', { country: country.name, strong })}</p>
                <p>{t('resultsInOtherCountries')}</p>
              </>
            )}
          </Message>
          {(!showOtherBrands || !otherBrands.length) && (
            <Actions>
              <Button $size="lg" onClick={onReset}>
                {t('browseAll')}
              </Button>
              <Button $size="lg" $variant="outline" href={config.addYourBusinessUrl}>
                {t('addBusiness')}
              </Button>
              <Button $size="lg" $variant="outline" href={config.brandRequestUrl}>
                {t('requestBrand')}
              </Button>
            </Actions>
          )}
        </EmptyState>
      )}
      {loading && (
        <LoadingState>
          <Loader />
        </LoadingState>
      )}
      {!loading && !!brands.length && (
        <Grid>
          {brands.map((brand, index) => (
            <BrandCard key={brand.id} brand={brand} priority={index < priorityCount} />
          ))}
        </Grid>
      )}
      {!loading && showOtherBrands && !!otherGiftcards.length && (
        <OtherBrands>
          {!!brands.length && <OtherBrandsTitle>{t('alsoInOtherCountries')}</OtherBrandsTitle>}
          <Grid>
            {otherGiftcards.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </Grid>
        </OtherBrands>
      )}
      {!loading && showOtherBrands && !!otherTopups.length && (
        <OtherBrands>
          {(!!otherGiftcards.length || !!brands.length) && (
            <OtherBrandsTitle>
              {(!brands.length || (!!otherGiftcards.length && !!brands.length)) &&
                t('andMobileTopups')}
              {!otherGiftcards.length && t('alsoMobileTopups')}
            </OtherBrandsTitle>
          )}
          <Grid>
            {otherTopups.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </Grid>
        </OtherBrands>
      )}
    </Container>
  );
};

export default BrandGrid;
